import { z } from "zod";
import { zWhereabouts } from "./whereabouts";
import { zImageReference } from "./imageReferenceSchema";

// Independent of performances.ts so scripts, account Items and live projections
// all use the same vocabulary without depending on one another's storage model.
const id = z
  .string()
  .min(1)
  .max(100)
  .regex(/^[a-zA-Z0-9_-]+$/);
export const zStoryItemProperties = z.object({
  name: z.string().min(1).max(120),
  description: z.string().min(1).max(1200),
  icon: z.string().min(1).max(32),
  abilities: z
    .array(
      z.object({
        id,
        name: z.string().min(1).max(100),
        effect: z.string().min(1).max(700),
      })
    )
    .max(8),
  limitations: z.array(z.string().min(1).max(500)).max(8),
  origin: z.string().max(700).optional(),
  /** A use spends one charge. Omit for a reusable object, never for unlimited magic. */
  maxUses: z.number().int().min(1).max(1000).optional(),
});
export type StoryItemProperties = z.infer<typeof zStoryItemProperties>;
export const zStoryItemDefinition = zStoryItemProperties.extend({
  id,
  /** Complete reviewed image reference from the account Item, including metadata and panels. */
  visual: zImageReference.optional(),
  /** Account-local Item reference. Never sent to guest screens or invented by a writer. */
  catalogItemId: id.optional(),
  catalogAccountId: id.optional(),
});
export type StoryItemDefinition = z.infer<typeof zStoryItemDefinition>;
export const zStoryItem = zStoryItemDefinition.extend({
  ownerId: id.optional(),
  /** Condition is independent of possession. No owner means the item is placed. */
  condition: z.enum(["intact", "consumed", "broken"]),
  location: zWhereabouts.optional(),
  usesRemaining: z.number().int().min(0).max(1000).optional(),
});
export type StoryItem = z.infer<typeof zStoryItem>;
export const zStoryItemCue = z.object({
  kind: z.literal("item"),
  action: z.enum(["discover", "reveal", "use", "give", "lose", "break", "repair", "consume"]),
  itemId: id,
  character: id,
  toCharacter: id.optional(),
  abilityId: id.optional(),
  detail: z.string().max(700).optional(),
  /** Where an object is found or left; other actions cannot rewrite its whereabouts. */
  location: zWhereabouts.optional(),
  item: zStoryItemDefinition.optional(),
});
export type StoryItemCue = z.infer<typeof zStoryItemCue>;

/** Parse rather than spread: these are public story facts, not account possessions. */
export function projectStoryItems(items: readonly StoryItem[]): StoryItem[] {
  return items.map((item) => {
    const { catalogItemId: _source, catalogAccountId: _owner, ...safe } = zStoryItem.parse(item);
    return safe;
  });
}

export function validateStoryItems(items: readonly StoryItem[], characters?: readonly string[]): void {
  if (items.length > 200) throw new Error("A story item checkpoint can hold at most 200 items");
  const ids = new Set<string>();
  for (const raw of items) {
    const item = zStoryItem.parse(raw);
    if (ids.has(item.id)) throw new Error(`Duplicate story item: ${item.id}`);
    ids.add(item.id);
    if (new Set(item.abilities.map((ability) => ability.id)).size !== item.abilities.length)
      throw new Error(`Duplicate ability on ${item.id}`);
    if (item.ownerId && characters && !characters.includes(item.ownerId))
      throw new Error(`Unknown item owner: ${item.ownerId}`);
    if (item.condition === "consumed" && item.ownerId)
      throw new Error(`${item.id} cannot be carried after it is consumed`);
    if (item.ownerId && item.location)
      throw new Error(`${item.id} must be held by a character or placed at a location, not both`);
    if (item.usesRemaining !== undefined && (item.maxUses === undefined || item.usesRemaining > item.maxUses))
      throw new Error(`Invalid remaining uses for ${item.id}`);
    if (item.usesRemaining === 0 && item.condition !== "consumed") throw new Error(`${item.id} has been used up`);
  }
}

/** Immutable replay: a sealed cue is the only way to change an established item. */
export function applyStoryItemCue(
  input: readonly StoryItem[],
  raw: StoryItemCue,
  characters?: readonly string[]
): StoryItem[] {
  const cue = zStoryItemCue.parse(raw);
  validateStoryItems(input, characters);
  if (characters && !characters.includes(cue.character)) throw new Error(`Unknown item actor: ${cue.character}`);
  if (cue.toCharacter && (cue.action !== "give" || (characters && !characters.includes(cue.toCharacter))))
    throw new Error("An item recipient must be a cast member receiving a gift");
  if (cue.item && cue.action !== "discover" && cue.action !== "reveal")
    throw new Error("Only discoveries or revelations can define an item");
  if (cue.abilityId && cue.action !== "use") throw new Error("An ability can only be selected for a Use item cue.");
  if (cue.location !== undefined && cue.action !== "discover" && cue.action !== "lose")
    throw new Error("Only discovery and loss cues can record an item location");
  const current = input.find((item) => item.id === cue.itemId);
  if (cue.action === "discover") {
    if (current) {
      if (cue.item) throw new Error("A rediscovered item must keep its original definition");
      if (current.ownerId || current.condition === "consumed") throw new Error(`${current.name} cannot be found here`);
      return input.map((item) => {
        if (item.id !== cue.itemId) return item;
        const { location: _previousLocation, ...rest } = item;
        return {
          ...rest,
          ownerId: cue.character,
        };
      });
    }
    if (!cue.item || cue.item.id !== cue.itemId) throw new Error("A new discovery needs its matching item definition");
    if (cue.item.catalogItemId || cue.item.catalogAccountId)
      throw new Error("A story discovery cannot grant an account Item");
    const item: StoryItem = {
      ...cue.item,
      ownerId: cue.character,
      condition: "intact",
      ...(cue.item.maxUses === undefined ? {} : { usesRemaining: cue.item.maxUses }),
    };
    const result = [...input, item];
    validateStoryItems(result, characters);
    return result;
  }
  if (!current) throw new Error(`Unknown story item: ${cue.itemId}`);
  if (current.ownerId !== cue.character) throw new Error(`${cue.character} is not carrying ${current.name}`);
  let next: StoryItem = { ...current };
  if (cue.action === "reveal") {
    const definition = cue.item;
    if (!definition || !cue.detail?.trim()) throw new Error("Show how the character learns about this item");
    if (
      current.catalogItemId ||
      current.catalogAccountId ||
      definition.catalogItemId ||
      definition.catalogAccountId ||
      definition.id !== current.id ||
      definition.maxUses !== current.maxUses
    )
      throw new Error("Revelations cannot change an account item, its identity or its charges");
    if (
      current.abilities.some(
        (old) =>
          !definition.abilities.some(
            (ability) => ability.id === old.id && ability.name === old.name && ability.effect === old.effect
          )
      )
    )
      throw new Error("Revelations must preserve established abilities");
    if (current.limitations.some((limit) => !definition.limitations.includes(limit)))
      throw new Error("Revelations must preserve established limitations");
    next = { ...current, ...definition };
  } else if (cue.action === "give") {
    if (!cue.toCharacter || cue.toCharacter === cue.character)
      throw new Error("Choose another character to receive the item");
    next.ownerId = cue.toCharacter;
    delete next.location;
  } else if (cue.action === "lose" || cue.action === "consume") {
    if (cue.action === "consume") next.condition = "consumed";
    delete next.ownerId;
    delete next.location;
    if (cue.action === "lose" && cue.location !== undefined) next.location = cue.location;
    if (cue.action === "consume" && next.maxUses !== undefined) next.usesRemaining = 0;
  } else if (cue.action === "break") {
    if (current.condition !== "intact") throw new Error(`${current.name} is already broken`);
    next.condition = "broken";
  } else if (cue.action === "repair") {
    if (current.condition !== "broken") throw new Error(`${current.name} does not need repair`);
    next.condition = "intact";
  } else if (cue.action === "use") {
    if (current.condition !== "intact") throw new Error(`${current.name} cannot be used while ${current.condition}`);
    if (!cue.abilityId || !current.abilities.some((ability) => ability.id === cue.abilityId))
      throw new Error(`Use an established ability of ${current.name}`);
    if (current.maxUses !== undefined) {
      next.usesRemaining = (current.usesRemaining ?? current.maxUses) - 1;
      if (next.usesRemaining <= 0) {
        next.usesRemaining = 0;
        next.condition = "consumed";
        delete next.ownerId;
        delete next.location;
      }
    }
  }
  const result = input.map((item) => (item.id === cue.itemId ? next : item));
  validateStoryItems(result, characters);
  return result;
}

/** Match playback within a sealed beat: earlier delays first, source order for ties. */
export function storyItemCues(beat: {
  actions?: readonly unknown[];
  cue?: unknown;
  together?: readonly unknown[];
  parallel?: readonly unknown[];
}): StoryItemCue[] {
  return [...(beat.actions ?? []), beat.cue, ...(beat.parallel ?? []), ...(beat.together ?? [])]
    .map((cue, index) => ({ cue, index }))
    .filter(
      (entry): entry is { cue: StoryItemCue & { delayStart?: number }; index: number } =>
        !!entry.cue && typeof entry.cue === "object" && "kind" in entry.cue && entry.cue.kind === "item"
    )
    .sort(
      (a, b) =>
        Math.max(0, (a.cue as any).delayMs ?? a.cue.delayStart ?? 0) -
          Math.max(0, (b.cue as any).delayMs ?? b.cue.delayStart ?? 0) || a.index - b.index
    )
    .map(({ cue }) => zStoryItemCue.parse(cue));
}
